// Work tree of one task (RF-UI-12, RF-OBS-12/13, AC-71): task → attempt → agent → step →
// call/tool from GET /v1/tasks/{id}/trace, enriched per attempt by /attempts/{id}/backend
// (effective model, usage, the attempt it re-runs), plus the Gauntlet rounds and Maestro
// reviews. A failed attempt stays failed and is linked to the attempt that re-ran it; open
// spans show elapsed time with precision "incomplete"; unknown usage is labelled unknown.
import { useEffect, useState } from "react";
import { useI18n } from "../../i18n/index.tsx";
import { getAttemptBackend, getGauntlet, getMaestroReviews, getTrace, listAgentProfiles } from "../../api/endpoints-wave2.ts";
import type { AttemptBackend, GauntletResponse, MaestroReviewsResponse, TraceAttempt, TraceNode, TraceResponse, UsageSummary } from "../../api/types-wave2.ts";
import { STEP_VIEW, type StepState } from "../../lib/status.ts";
import { elapsedMs, formatCents, formatMs, formatNumber, isPrecision, useNow } from "../../lib/format.ts";
import { StatusIcon } from "../../components/Status.tsx";
import { ErrorBanner, LoadingBlock } from "../../components/Feedback.tsx";

/** Attempt/span status → the seven work-in-progress states (RF-UI-12). */
export function runStateOf(state: string): StepState {
  switch (state) {
    case "succeeded":
    case "completed":
    case "passed":
    case "done":
    case "ok":
    case "finished":
      return "done";
    case "failed":
    case "error":
    case "interrupted":
    case "timed_out":
    case "rejected":
      return "failed";
    case "blocked":
      return "blocked";
    case "paused":
      return "paused";
    case "cancelled":
    case "canceled":
    case "superseded":
      return "cancelled";
    case "running":
    case "in_progress":
    case "active":
    case "started":
      return "in_progress";
    default:
      return "waiting";
  }
}

const profileNames = new Map<string, Promise<Map<string, string>>>();

/** Agent profile names for a project (global + project), cached per project; empty when not readable. */
export function useProfileNames(projectId: string | null): Map<string, string> {
  const [names, setNames] = useState<Map<string, string>>(new Map());
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    let pending = profileNames.get(projectId);
    if (!pending) {
      pending = listAgentProfiles(projectId)
        .then((r) => new Map(r.items.map((p) => [p.id, p.name])))
        .catch(() => new Map<string, string>());
      profileNames.set(projectId, pending);
    }
    void pending.then((m) => {
      if (!cancelled) setNames(m);
    });
    return () => {
      cancelled = true;
    };
  }, [projectId]);
  return names;
}

export function UsageLine({ usage }: { usage: UsageSummary }) {
  const { t, locale } = useI18n();
  if (usage.calls === 0) return <span className="subtle">{t("trace.noCalls")}</span>;
  const c = usage.cost;
  const tok = (x: { known: number; coverage: { known: number; total: number } }) =>
    x.coverage.known === 0 ? t("usage.unknown") : x.coverage.known < x.coverage.total ? t("usage.partial", { n: formatNumber(x.known, locale), k: x.coverage.known, total: x.coverage.total }) : formatNumber(x.known, locale);
  return (
    <span className="usage-line" data-testid="usage-line">
      <span>{t("usage.inputTokens", { value: tok(usage.tokens.input) })}</span>
      <span>{t("usage.outputTokens", { value: tok(usage.tokens.output) })}</span>
      <span>
        {c.totalCents !== null
          ? t("usage.costTotal", { value: formatCents(c.totalCents, locale) })
          : c.knownCents > 0
            ? t("usage.costPartial", { value: formatCents(c.knownCents, locale), n: c.unknownCostCalls })
            : t("usage.costUnknown", { n: c.unknownCostCalls })}
        {c.estimatedCents > 0 ? <span className="badge">{t("usage.estimatedPart", { value: formatCents(c.estimatedCents, locale) })}</span> : null}
      </span>
      <span className="badge">{isPrecision(usage.precision) ? t(`precision.${usage.precision}`) : usage.precision}</span>
    </span>
  );
}

function TimeRange({ start, end, precision, now }: { start: string; end: string | null; precision?: string; now: number }) {
  const { t, formatTime } = useI18n();
  const ms = elapsedMs(start, end, now);
  return (
    <span className="time-range">
      <span>
        {formatTime(start)} → {end ? formatTime(end) : t("trace.running")}
      </span>
      {ms !== null ? <span className="mono">{end ? t("trace.duration", { value: formatMs(ms) }) : t("trace.elapsed", { value: formatMs(ms) })}</span> : null}
      {precision ? <span className="badge">{isPrecision(precision) ? t(`precision.${precision}`) : precision}</span> : !end ? <span className="badge">{t("precision.incomplete")}</span> : null}
    </span>
  );
}

function SpanNode({ node, now, depth }: { node: TraceNode; now: number; depth: number }) {
  const { t } = useI18n();
  const view = STEP_VIEW[runStateOf(node.status)];
  return (
    <li className="span-node" data-testid="span-node" data-span-kind={node.kind} data-state={runStateOf(node.status)}>
      <div className="span-head">
        <span className={`tone-${view.tone}`} aria-hidden="true">
          <StatusIcon kind={view.icon} size={14} />
        </span>
        <span className="badge mono">{node.kind}</span>
        <span className="user-content">{node.name}</span>
        <span className={`tone-${view.tone}`}>{t(view.label)}</span>
        {node.role ? <span className="subtle">{t("trace.role", { role: node.role })}</span> : null}
        {node.model ? <span className="subtle mono">{node.model}</span> : null}
        <span className="span-time">
          <span className="mono">{node.open ? t("trace.elapsed", { value: formatMs(elapsedMs(node.started_at, null, now) ?? node.duration_ms) }) : t("trace.duration", { value: formatMs(node.duration_ms) })}</span>
          <span className="badge">{isPrecision(node.precision) ? t(`precision.${node.precision}`) : node.precision}</span>
        </span>
      </div>
      {node.children.length > 0 && depth < 8 ? (
        <ol className="span-tree">
          {node.children.map((c) => (
            <SpanNode key={c.span_id} node={c} now={now} depth={depth + 1} />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

function effectiveModel(b: AttemptBackend | undefined): string | null {
  if (!b) return null;
  const reported = b.calls.map((c) => c.effective_model).filter((m): m is string => typeof m === "string" && m.length > 0);
  return reported.at(-1) ?? null;
}

function AttemptNode({
  attempt,
  backend,
  spans,
  byId,
  fixedBy,
  agentName,
  now,
}: {
  attempt: TraceAttempt;
  backend: AttemptBackend | undefined;
  spans: TraceNode[];
  byId: Map<string, TraceAttempt>;
  fixedBy: TraceAttempt | undefined;
  agentName: string | null;
  now: number;
}) {
  const { t } = useI18n();
  const state = runStateOf(attempt.state);
  const view = STEP_VIEW[state];
  // The trace carries the links now; the backend view is the fallback for older builds.
  const supersedes = attempt.supersedes_attempt_id ?? backend?.supersedes_attempt_id ?? null;
  const endReason = attempt.end_reason ?? backend?.end_reason ?? null;
  const callsList = backend?.coverage.calls_list;
  const previous = supersedes ? byId.get(supersedes) : undefined;
  const model = effectiveModel(backend);
  const pinned = typeof backend?.pinned_route["external_model_id"] === "string" ? (backend.pinned_route["external_model_id"] as string) : null;
  return (
    <li className={`attempt-node attempt-node--${view.tone}`} id={`attempt-${attempt.id}`} data-testid="attempt-node" data-state={state} data-attempt={attempt.number}>
      <div className="attempt-head">
        <span className={`step-icon tone-${view.tone}`} aria-hidden="true">
          <StatusIcon kind={view.icon} size={14} />
        </span>
        <strong>{t("trace.attemptN", { n: attempt.number })}</strong>
        <span className={`tone-${view.tone}`}>{t(view.label)}</span>
        <span className="badge">{attempt.role}</span>
        {agentName ? <span className="subtle">{t("trace.agent", { name: agentName })}</span> : null}
        <span className="subtle mono" data-testid="effective-model">
          {model ? t("trace.effectiveModel", { model }) : pinned ? t("trace.pinnedModel", { model: pinned }) : t("trace.modelNotReported")}
        </span>
        <TimeRange start={attempt.started_at} end={attempt.ended_at} now={now} />
      </div>
      <div className="attempt-meta">
        {previous ? (
          <a href={`#attempt-${previous.id}`} className="link-subtle" data-testid="attempt-supersedes">
            {t("trace.rerunOf", { n: previous.number })}
          </a>
        ) : null}
        {fixedBy ? (
          <a href={`#attempt-${fixedBy.id}`} className="link-subtle" data-testid="attempt-fixed-by">
            {t("trace.fixedBy", { n: fixedBy.number })}
          </a>
        ) : null}
        {endReason ? <span className={`tone-${view.tone} user-content`}>{endReason}</span> : null}
        {backend && backend.model_divergences.length > 0 ? (
          <span className="tone-warning">{t("trace.divergence", { requested: backend.model_divergences[0]!.requested, effective: backend.model_divergences[0]!.effective })}</span>
        ) : null}
        {backend ? <UsageLine usage={backend.usage} /> : <span className="subtle">{t("trace.usageLoading")}</span>}
        {callsList?.truncated ? <span className="subtle">{t("trace.callsTruncated", { n: callsList.calls_included, total: callsList.calls_total })}</span> : null}
      </div>
      {spans.length > 0 ? (
        <ol className="span-tree">
          {spans.map((s) => (
            <SpanNode key={s.span_id} node={s} now={now} depth={0} />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

function GauntletView({ data, byId }: { data: GauntletResponse; byId: Map<string, TraceAttempt> }) {
  const { t, locale } = useI18n();
  if (data.runs.length === 0) return null;
  const attemptLabel = (id: string | null) => {
    if (!id) return "—";
    const a = byId.get(id);
    return a ? `#${a.number}` : "…";
  };
  return (
    <div className="stack" style={{ gap: 6 }} data-testid="gauntlet-view">
      {data.runs.map((run) => {
        const current = run.rounds.at(-1);
        const finding = run.rounds
          .flatMap((r) => r.findings)
          .filter((f) => f.verdict !== "pass")
          .at(-1);
        return (
          <div key={run.id} className="gauntlet-run">
            <p className="row row--wrap">
              <strong>{t("trace.gauntletRound", { n: current?.number ?? 0, max: run.max_rounds })}</strong>
              <span className="badge">{run.state}</span>
              {run.stop_reason ? <span className="subtle">{run.stop_reason}</span> : null}
              <span className="subtle">
                {run.totals.unknown_cost_rounds > 0
                  ? t("trace.gauntletCostPartial", { value: formatCents(run.totals.known_cost_cents, locale), n: run.totals.unknown_cost_rounds })
                  : t("trace.gauntletCost", { value: formatCents(run.totals.known_cost_cents, locale) })}
              </span>
            </p>
            <ol className="plain-list">
              {run.rounds.map((r) => (
                <li key={r.id} className="row row--wrap" data-testid="gauntlet-round">
                  <span className="badge">{t("trace.roundN", { n: r.number })}</span>
                  <span>{t("trace.builderCritic", { builder: attemptLabel(r.builder_attempt_id), critic: attemptLabel(r.critic_attempt_id) })}</span>
                  <span className="subtle">{r.state}</span>
                  {r.resolved_criteria !== null ? <span className="subtle">{t("trace.resolvedCriteria", { n: r.resolved_criteria })}</span> : null}
                </li>
              ))}
            </ol>
            {finding ? (
              <p className="tone-warning user-content" data-testid="gauntlet-finding">
                {t("trace.mainFinding", { text: finding.observed_failure ?? finding.resolution_condition ?? finding.verdict })}
              </p>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function ReviewsView({ data }: { data: MaestroReviewsResponse }) {
  const { t, formatDateTime, locale } = useI18n();
  if (data.items.length === 0) return null;
  return (
    <div className="stack" style={{ gap: 4 }} data-testid="maestro-reviews">
      <h4 className="subheading">{t("trace.maestroReviews")}</h4>
      <ul className="plain-list">
        {data.items.map((r) => (
          <li key={r.id} className="row row--wrap">
            <span className="badge">{r.verdict ?? r.state}</span>
            {r.epoch !== null ? <span className="subtle">{t("trace.epoch", { n: r.epoch })}</span> : null}
            {r.review_model ? <span className="mono subtle">{r.review_model}</span> : null}
            <span className="subtle">{r.cost_cents === null ? t("usage.costUnknownShort") : formatCents(r.cost_cents, locale)}</span>
            <span className="subtle">{formatDateTime(r.created_at)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface TraceBundle {
  trace: TraceResponse;
  gauntlet: GauntletResponse | null;
  reviews: MaestroReviewsResponse | null;
}

/** Loads and renders the work tree of a task; `version` changes trigger a quiet refresh. */
export function TraceView({ taskId, projectId, version }: { taskId: string; projectId: string | null; version?: number | string }) {
  const { t, formatTime } = useI18n();
  const now = useNow(1000);
  const [bundle, setBundle] = useState<TraceBundle | null>(null);
  const [backends, setBackends] = useState<Record<string, AttemptBackend>>({});
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);
  const names = useProfileNames(projectId);

  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    Promise.all([
      getTrace(taskId, controller.signal),
      getGauntlet(taskId, controller.signal).catch(() => null),
      getMaestroReviews(taskId, controller.signal).catch(() => null),
    ])
      .then(([trace, gauntlet, reviews]) => {
        if (controller.signal.aborted) return;
        setBundle({ trace, gauntlet, reviews });
        for (const a of trace.attempts) {
          getAttemptBackend(a.id, controller.signal)
            .then((b) => !controller.signal.aborted && setBackends((m) => ({ ...m, [a.id]: b })))
            .catch(() => undefined);
        }
      })
      .catch((e: unknown) => {
        if (!controller.signal.aborted && !(e instanceof DOMException && e.name === "AbortError")) setError(e);
      });
    return () => controller.abort();
  }, [taskId, version, attempt]);

  if (error && !bundle) return <ErrorBanner error={error} onRetry={() => setAttempt((n) => n + 1)} title={t("trace.loadFailed")} />;
  if (!bundle) return <LoadingBlock label={t("common.loading")} lines={2} />;
  const { trace, gauntlet, reviews } = bundle;
  const byId = new Map(trace.attempts.map((a) => [a.id, a]));
  const supersededBy = new Map<string, TraceAttempt>();
  for (const a of trace.attempts) {
    const prev = a.supersedes_attempt_id ?? backends[a.id]?.supersedes_attempt_id ?? null;
    if (prev) supersededBy.set(prev, a);
  }
  const spansOf = (attemptId: string | null) => trace.spans.filter((s) => s.attempt_id === attemptId);
  const loose = trace.spans.filter((s) => s.attempt_id === null || !byId.has(s.attempt_id));

  return (
    <div className="stack trace-view" style={{ gap: 8 }} data-testid="trace-view">
      {trace.attempts.length === 0 && trace.spans.length === 0 ? (
        <p className="subtle">{t("trace.noAttempts")}</p>
      ) : (
        <ol className="attempt-tree" aria-label={t("trace.attempts")}>
          {trace.attempts.map((a) => {
            const b = backends[a.id];
            return (
              <AttemptNode
                key={a.id}
                attempt={a}
                backend={b}
                spans={spansOf(a.id)}
                byId={byId}
                fixedBy={runStateOf(a.state) === "failed" ? supersededBy.get(a.id) : undefined}
                agentName={(() => {
                  const profileId = a.agent_profile_id ?? b?.agent_profile_id ?? null;
                  return profileId ? (names.get(profileId) ?? null) : null;
                })()}
                now={now}
              />
            );
          })}
        </ol>
      )}
      {loose.length > 0 ? (
        <div className="stack" style={{ gap: 4 }}>
          <h4 className="subheading">{t("trace.otherSteps")}</h4>
          <ol className="span-tree">
            {loose.map((s) => (
              <SpanNode key={s.span_id} node={s} now={now} depth={0} />
            ))}
          </ol>
        </div>
      ) : null}
      {gauntlet ? <GauntletView data={gauntlet} byId={byId} /> : null}
      {reviews ? <ReviewsView data={reviews} /> : null}
      <p className="field-hint">
        {t("trace.generatedAt", { time: formatTime(trace.generated_at) })}
        {trace.orphan_spans > 0 ? ` · ${t("trace.orphans", { n: trace.orphan_spans })}` : ""}
      </p>
    </div>
  );
}
